import { useIsMobile } from '@dapp/multi-lib/ui/shared-hooks/useIsMobile';
import { useToast } from '@dapp/multi-lib/ui/toast';
import { MessageStatus } from 'structure/bridge/constants/messageStatus';
import { useAtom } from 'jotai';
import { useEffect } from 'react';
import {
  layerZeroChainIdsByChainId,
  lzDefaultAdapterParams,
} from 'structure/bridge/constants/lazyerZeroChains';
import { bridgeAtom } from 'structure/bridge/store/bridgeAtom';
import {
  TTransaction,
  transactionsAtom,
} from 'structure/bridge/store/transactionsAtom';
import { Address, encodeAbiParameters, parseAbiParameters } from 'viem';
import { useReadContract, useWriteContract, useSimulateContract } from 'wagmi';
import PROXY_OFTV2_ABI from '../../constants/abis/ProxyOFTV2WithFee';

interface Props {
  transaction: TTransaction;
}

const defaultEstimatedFee: [bigint, bigint] = [BigInt(0), BigInt(0)];

export function BridgeFailedTransactionReverseButton({ transaction }: Props) {
  const isMobile = useIsMobile();
  const toastV2 = useToast();
  const [bridgeState] = useAtom(bridgeAtom);
  const [transactions, setTransactions] = useAtom(transactionsAtom);

  // Get native fee estimate
  const {
    data: estimatedSendFee = defaultEstimatedFee,
    error: estimatedSendFeeError,
  } = useReadContract({
    query: {
      enabled: !!transaction.token?.oftAddress[transaction.dstChainId],
    },
    chainId: transaction.dstChainId,
    address: transaction.token.oftAddress[transaction.dstChainId],
    abi: PROXY_OFTV2_ABI,
    functionName: 'estimateSendFee',
    args: [
      layerZeroChainIdsByChainId[bridgeState.dstChainId],
      encodeAbiParameters(parseAbiParameters('address'), [
        transaction.userAddress,
      ]) as Address, // to address encoded
      BigInt(transaction.amount), // token amount to send
      false, // use zero token
      lzDefaultAdapterParams, // adapter params
    ],
  });

  useEffect(() => {
    if (estimatedSendFeeError) {
      console.error('Error estimating send fee:', estimatedSendFeeError);
    }
  }, [estimatedSendFeeError]);

  const reverseMessageSimulation = useSimulateContract({
    chainId: transaction.dstChainId,
    address: transaction.token.oftAddress[transaction.dstChainId],
    abi: PROXY_OFTV2_ABI,
    functionName: 'reverseMessage',
    args: [
      Number(transaction?.failedMessage?._srcChainId ?? 0),
      (transaction?.failedMessage?._srcAddress ?? '0x') as `0x${string}`,
      BigInt(transaction?.failedMessage?._nonce ?? 0),
      (transaction?.failedMessage?._payload ?? '0x') as `0x${string}`,
    ],
    value: estimatedSendFee[0],
    query: {
      enabled:
        estimatedSendFee[0] > 0 &&
        !!transaction.token?.oftAddress[transaction.dstChainId],
    },
  });

  const { writeContractAsync: reverse } = useWriteContract();

  const handleRevert = async () => {
    try {
      const txHash = await reverse(
        reverseMessageSimulation.data!.request as Parameters<typeof reverse>[0]
      );
      const newTransaction: TTransaction = {
        status: MessageStatus.INFLIGHT,
        userAddress: transaction.userAddress,
        srcChainId: transaction.dstChainId,
        dstChainId: transaction.srcChainId,
        toAddress: transaction.toAddress,
        amount: transaction.amount,
        minAmount: transaction.minAmount,
        srcTxHash: txHash,
        dstTxHash: '0x',
        nativeFee: estimatedSendFee[0].toString(),
        token: transaction.token,
        initialNonce: transaction.initialNonce,
      };
      const cleanedTransactions = transactions.filter(
        (tx) => tx.srcTxHash !== transaction.srcTxHash
      );
      setTransactions([...cleanedTransactions, newTransaction]);
      toastV2({
        title: 'Reverted!',
        type: 'success',
        description: `Transaction ${transaction.dstTxHash} has been retried.`,
      });
    } catch (error) {
      console.log('### error', error);
    }
  };

  return (
    <button
      title='Retry transaction '
      className={`border-1 whitespace-nowrap rounded-xl border ${isMobile ? 'px-2 py-0 text-[8px]' : 'px-3 py-1'} text-white`}
      onClick={handleRevert}
    >
      Revert
    </button>
  );
}
